import type { DailyWorkDto } from '../api/attendance';

/**
 * 「내 근태 상세보기」가 쓰는 **순수 셈** — 주간 · 월간 · 하루 띠 (2026-09-06 · 판 B).
 *
 * ★★★ **화면에서 셈하지 않는다.** 같은 값을 요약 막대 · 달력 칸 · 하루 띠 셋이 읽는데,
 *    셋이 제 손으로 세면 같은 하루가 자리마다 달라진다. 여기 한 곳에서 낸다.
 * ★★ **DB 도 DOM 도 없다** — 규칙을 시험하는 데 화면이 필요하면 아무도 시험하지 않는다.
 */

/** 주 52시간 — **계약이 아니라 «법정 상한»** 이다(소정 40 + 연장 12 · 근기법 §53). */
export const WEEK_CAP_MINUTES = 52 * 60;
/** 소정 40시간 — 막대의 눈금 하나가 여기 선다. */
export const WEEK_BASE_MINUTES = 40 * 60;

export type PeriodRange = 'week' | 'month';

const DAY = 86_400_000;

export function toKey(d: Date): string {
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${String(d.getUTCDate()).padStart(2, '0')}`;
}
export function fromKey(key: string): Date {
  return new Date(`${key}T00:00:00.000Z`);
}

/** 월요일이 첫 칸이다 — 한국의 근무 주는 월~일로 읽힌다. */
export function weekStart(anchor: Date): Date {
  const d = new Date(Date.UTC(anchor.getUTCFullYear(), anchor.getUTCMonth(), anchor.getUTCDate()));
  const dow = (d.getUTCDay() + 6) % 7; // 월=0
  return new Date(d.getTime() - dow * DAY);
}

/** 그 기간의 날짜 키들 — 주간은 일곱, 월간은 그 달 전부. */
export function periodKeys(range: PeriodRange, anchor: Date): string[] {
  if (range === 'week') {
    const s = weekStart(anchor);
    return Array.from({ length: 7 }, (_, i) => toKey(new Date(s.getTime() + i * DAY)));
  }
  const y = anchor.getUTCFullYear();
  const m = anchor.getUTCMonth();
  const last = new Date(Date.UTC(y, m + 1, 0)).getUTCDate();
  return Array.from({ length: last }, (_, i) => toKey(new Date(Date.UTC(y, m, i + 1))));
}

/**
 * 그 기간이 걸치는 **달들** — 주가 달을 넘으면 둘이다(8/31 ~ 9/6).
 * ★ 서버의 일별 집계는 **달 단위**라, 걸친 달을 다 불러야 한 주가 채워진다.
 */
export function monthsOf(keys: readonly string[]): Array<{ year: number; month: number }> {
  const seen = new Map<string, { year: number; month: number }>();
  for (const k of keys) {
    const y = Number(k.slice(0, 4));
    const m = Number(k.slice(5, 7));
    seen.set(`${y}-${m}`, { year: y, month: m });
  }
  return [...seen.values()];
}

export function hhmmToMin(hhmm: string | null | undefined): number | null {
  if (!hhmm) return null;
  const m = /^(\d{1,2}):(\d{2})$/.exec(hhmm);
  if (!m) return null;
  return Number(m[1]) * 60 + Number(m[2]);
}

/** `08:00` 형태 — 표에서 자리가 흔들리지 않게 앞자리를 채운다(F18). */
export function minToHHMM(min: number): string {
  const t = Math.max(0, Math.round(min));
  return `${String(Math.floor(t / 60) % 24).padStart(2, '0')}:${String(t % 60).padStart(2, '0')}`;
}

/** `12:30` — **기간**은 앞자리를 안 채운다(시각과 다른 값이라 눈으로 갈려야 한다 · F18). */
export function spanText(minutes: number): string {
  const t = Math.max(0, Math.round(minutes));
  return `${Math.floor(t / 60)}:${String(t % 60).padStart(2, '0')}`;
}

export type SegKind = 'work' | 'ot' | 'un' | 'night' | 'leave';

export interface DaySeg {
  kind: SegKind;
  /** 자정부터의 분. */
  from: number;
  to: number;
}

interface Iv { from: number; to: number }

function clip(iv: Iv): Iv | null {
  const from = Math.max(0, iv.from);
  const to = Math.min(24 * 60, iv.to);
  return to > from ? { from, to } : null;
}

/** a 에서 b 들을 뺀다 — 「미신청 체류」는 «머문 시간 − 승인된 연장» 이다. */
function subtract(a: Iv, bs: readonly Iv[]): Iv[] {
  let out: Iv[] = [a];
  for (const b of bs) {
    const next: Iv[] = [];
    for (const p of out) {
      if (b.to <= p.from || b.from >= p.to) { next.push(p); continue; }
      if (b.from > p.from) next.push({ from: p.from, to: b.from });
      if (b.to < p.to) next.push({ from: b.to, to: p.to });
    }
    out = next;
  }
  return out.filter((p) => p.to > p.from);
}

/** 야간 창 — 22:00~24:00 과 00:00~06:00. 서버 기준(`22:00-06:00`)과 같은 말이다. */
const NIGHT: Iv[] = [{ from: 22 * 60, to: 24 * 60 }, { from: 0, to: 6 * 60 }];

function intersect(a: Iv, bs: readonly Iv[]): Iv[] {
  const out: Iv[] = [];
  for (const b of bs) {
    const from = Math.max(a.from, b.from);
    const to = Math.min(a.to, b.to);
    if (to > from) out.push({ from, to });
  }
  return out;
}

/**
 * 하루를 **시각 축의 구간들**로 편다.
 *
 * ★★★ **휴게는 그리지 않는다.** 근무제에는 「60분」만 있고 **몇 시부터인지가 없다** —
 *    자리를 지어내면 띠가 거짓을 그린다. 휴게 «분» 은 한 줄이 적는다.
 * ★★ **야간은 겹치는 값**이라 다른 구간을 자르지 않는다 — 부르는 쪽이 «밑단» 에 깐다.
 * ★ 승인 연장은 **그날의 실제 시각**(`overtimeSpans`)에서 온다 — 분만으로는 어디였는지 모른다.
 */
export function daySegments(day: DailyWorkDto): DaySeg[] {
  const out: DaySeg[] = [];
  if (day.status === 'LEAVE') {
    const bs = hhmmToMin(day.baseStart) ?? 9 * 60;
    const be = hhmmToMin(day.baseEnd) ?? 18 * 60;
    return [{ kind: 'leave', from: bs, to: be }];
  }

  const inMin = hhmmToMin(day.checkIn);
  const outMin = hhmmToMin(day.checkOut);
  const baseEnd = hhmmToMin(day.baseEnd) ?? 18 * 60;

  const ot: Iv[] = [];
  for (const s of day.overtimeSpans ?? []) {
    const a = hhmmToMin(s.start);
    const b = hhmmToMin(s.end);
    if (a === null || b === null) continue;
    // 자정을 넘는 구간은 그날의 24:00 에서 끊는다 — 다음 날은 그 날의 띠가 그린다.
    const iv = clip({ from: a, to: b > a ? b : 24 * 60 });
    if (iv) ot.push(iv);
  }

  if (inMin !== null) {
    const end = outMin ?? baseEnd;
    // 업무 — 찍고부터 소정 종료까지. 연장은 아래에서 따로 그린다.
    const work = clip({ from: inMin, to: Math.min(end, baseEnd) });
    if (work) out.push({ kind: 'work', ...work });
    // 미신청 체류 — 소정 종료를 넘겨 남았는데 승인된 연장이 아닌 자리.
    if (outMin !== null && outMin > baseEnd) {
      for (const p of subtract({ from: baseEnd, to: outMin }, ot)) out.push({ kind: 'un', ...p });
    }
  }
  for (const p of ot) out.push({ kind: 'ot', ...p });

  // 야간 — 머문 구간과 야간 창의 교집합. 겹치는 값이라 맨 뒤에 둔다.
  if (inMin !== null && outMin !== null) {
    for (const p of intersect({ from: inMin, to: outMin }, NIGHT)) out.push({ kind: 'night', ...p });
  }
  return out.sort((a, b) => a.from - b.from);
}

export interface PeriodTotals {
  /** 인정 근로 + 승인 연장 — 막대가 재는 값이다. */
  totalMinutes: number;
  creditedMinutes: number;
  overtimeMinutes: number;
  unclaimedMinutes: number;
  /** 휴가 «일» — 시간과 다른 통이라 단위를 바꾸지 않는다. */
  leaveDays: number;
  /** 아직 오지 않은 근무일(예정) — 기록도 휴가도 없는 «지난 뒤가 아닌» 평일. */
  remainingWorkDays: number;
}

/**
 * 기간 합계.
 *
 * ★★★ **「잔여 근무일」은 근무제가 요일을 모르므로 «주말이 아닌 날»로 센다** —
 *    이 시스템은 어느 날이 근무일인지 모른다(그래서 「결근」도 못 센다). 화면이 그 사실을 적는다.
 */
export function periodTotals(
  keys: readonly string[], byDate: ReadonlyMap<string, DailyWorkDto>, todayKey: string,
): PeriodTotals {
  let credited = 0, overtime = 0, unclaimed = 0, leaveDays = 0, remaining = 0;
  for (const k of keys) {
    const d = byDate.get(k);
    if (d) {
      credited += d.creditedMinutes;
      overtime += d.overtimeMinutes;
      unclaimed += d.unclaimedMinutes;
      if (d.status === 'LEAVE') leaveDays += 1;
      continue;
    }
    const dow = fromKey(k).getUTCDay();
    if (k >= todayKey && dow !== 0 && dow !== 6) remaining += 1;
  }
  return {
    totalMinutes: credited + overtime,
    creditedMinutes: credited,
    overtimeMinutes: overtime,
    unclaimedMinutes: unclaimed,
    leaveDays,
    remainingWorkDays: remaining,
  };
}
